import { useCallback } from 'react';

import {
  InfiniteData,
  QueryClient,
  infiniteQueryOptions,
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { useSetAtom } from 'jotai';

import type { DTOFollowRequest } from 'src/apis/dto/DTOFollowRequest';
import { DTOProfileCard } from 'src/apis/dto/DTOProfileCard';
import useEnableRemoteConfig from 'src/hooks/useEnableRemoteConfig';
import { useProfileToggleFollowMutation } from 'src/hooks/useFollowQuery';
import useLogin from 'src/hooks/useLogin';
import { HistoryApiList } from 'src/stores/history/api';
import {
  DeleteHistoryCardRequest,
  DeleteHistoryCardResponse,
  GetHistoryCardsRequest,
  GetHistoryCardsResponse,
  GetHistoryFiltersResponse,
  GetProfileViewersResponse,
  GetRecommendedHistoryCardsResponse,
  HistoryCard,
  HistoryFilterSelection,
  ProfileViewCard,
} from 'src/stores/history/types';
import { showCommonErrorToastAtom } from 'src/stores/toast/atoms';
import { client } from 'src/utils/api';

export const HISTORY_QUERY_KEYS = {
  all: ['history'],
  cards: () => [...HISTORY_QUERY_KEYS.all, 'cards'],
  historyRecommended: () => [...HISTORY_QUERY_KEYS.cards(), 'recommended'],
  historyProfileUpdated: () => [...HISTORY_QUERY_KEYS.cards(), 'profileUpdated'],
  /**
   * 필터별 infinite 목록 전체에 공통으로 쓰이는 prefix.
   * `cards()` 하위에 여러 종류의 쿼리(recommended / profileUpdated / profileViewers 등)가
   * 공존하므로, `setQueriesData` 로 필터 목록만 타겟하려면 이 prefix 를 사용한다.
   */
  historyCardsAll: () => [...HISTORY_QUERY_KEYS.cards(), 'filtered'],
  historyCards: (historyFilterSelections: HistoryFilterSelection[]) => {
    return [
      ...HISTORY_QUERY_KEYS.historyCardsAll(),
      ...historyFilterSelections.map((selection) => selection.optionKey),
    ];
  },

  historyFilters: () => [...HISTORY_QUERY_KEYS.all, 'filters'],
};

export const getDefaultHistoryFilterSelections = (
  filters?: GetHistoryFiltersResponse
): HistoryFilterSelection[] =>
  filters?.map(({ defaultFilterOptionKey, filterId, filterOptionType }) => ({
    filterId,
    filterOptionType,
    optionKey: filterId === 'EVENT' ? 'MATCH' : defaultFilterOptionKey,
  })) ?? [];

const historyFiltersQueryOptions = () =>
  queryOptions({
    queryKey: HISTORY_QUERY_KEYS.historyFilters(),
    queryFn: () => client.get<GetHistoryFiltersResponse>(HistoryApiList.HistoryFilters),
  });

const recommendedHistoryCardsQueryOptions = () =>
  queryOptions({
    queryKey: HISTORY_QUERY_KEYS.historyRecommended(),
    queryFn: () =>
      client.get<GetRecommendedHistoryCardsResponse>(HistoryApiList.HistoryRecommended, {
        params: { size: 30 },
      }),
  });

const historyProfileUpdatedQueryOptions = () =>
  queryOptions({
    queryKey: HISTORY_QUERY_KEYS.historyProfileUpdated(),
    queryFn: () => client.get<HistoryCard[]>(HistoryApiList.HistoryProfileUpdated),
  });

const historyCardsInfiniteQueryOptions = (historyFilterSelections: HistoryFilterSelection[]) =>
  infiniteQueryOptions({
    queryKey: HISTORY_QUERY_KEYS.historyCards(historyFilterSelections),
    queryFn: async ({ pageParam }) => {
      const cursor = pageParam ?? undefined;

      const params: GetHistoryCardsRequest = {
        cursor,
        filterSelections: historyFilterSelections,
      };

      const response = await client.post<GetHistoryCardsResponse>(
        HistoryApiList.HistoryCards,
        params
      );

      return response.data;
    },
    initialPageParam: null as GetHistoryCardsResponse['nextCursor'] | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? null,
  });

export const usePrefetchHistoryData = () => {
  const { isLogin } = useLogin();
  const queryClient = useQueryClient();

  return useCallback(async () => {
    if (!isLogin) return;

    try {
      const [filtersResponse] = await Promise.all([
        queryClient.ensureQueryData(historyFiltersQueryOptions()),
        queryClient.prefetchQuery(recommendedHistoryCardsQueryOptions()),
      ]);

      const defaultSelections = getDefaultHistoryFilterSelections(filtersResponse.data);

      if (defaultSelections.length) {
        queryClient.prefetchInfiniteQuery(historyCardsInfiniteQueryOptions(defaultSelections));
      }
    } catch {
      // prefetch 실패는 무시 — 실제 렌더링 시 useQuery가 재시도합니다
    }
  }, [queryClient, isLogin]);
};

export const useGetHistoryFiltersQuery = () => {
  const { isLogin } = useLogin();

  return useQuery({
    ...historyFiltersQueryOptions(),
    enabled: isLogin,
  });
};

export const useGetRecommendedHistoryCardsQuery = () => {
  const { isLogin } = useLogin();

  return useQuery({
    ...recommendedHistoryCardsQueryOptions(),
    enabled: isLogin,
  });
};

export const useGetHistoryCardsInfiniteQuery = (
  historyFilterSelections: HistoryFilterSelection[]
) => {
  const { isLogin } = useLogin();

  return useInfiniteQuery({
    ...historyCardsInfiniteQueryOptions(historyFilterSelections),
    refetchOnMount: false,
    staleTime: Infinity,
    enabled: isLogin && !!historyFilterSelections.length,
  });
};

export const useGetHistoryProfileUpdatedQuery = () => {
  const { isLogin } = useLogin();

  return useQuery({
    ...historyProfileUpdatedQueryOptions(),
    enabled: isLogin,
  });
};

const PROFILE_VIEWERS_PAGE_SIZE = 20;

const profileViewersInfiniteQueryOptions = () =>
  infiniteQueryOptions({
    queryKey: [...HISTORY_QUERY_KEYS.cards(), 'profileViewers'],
    queryFn: async ({ pageParam }) => {
      const response = await client.post<GetProfileViewersResponse>(
        HistoryApiList.HistoryProfileViewers,
        undefined,
        {
          params: {
            size: PROFILE_VIEWERS_PAGE_SIZE,
            ...(pageParam ? { cursor: pageParam } : {}),
          },
        }
      );
      return response.data;
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? null,
  });

export const useGetViewedMeCardsQuery = () => {
  const { isLogin } = useLogin();
  const enableViewedMe = useEnableRemoteConfig('enableViewedMe');

  return useInfiniteQuery({
    ...profileViewersInfiniteQueryOptions(),
    enabled: isLogin && enableViewedMe === true,
  });
};

export const useDeleteProfileViewerCardMutation = () => {
  const queryClient = useQueryClient();
  const showCommonErrorToast = useSetAtom(showCommonErrorToastAtom);

  return useMutation({
    mutationFn: async (request: DeleteHistoryCardRequest) => {
      const response = await client.delete<DeleteHistoryCardResponse>(HistoryApiList.HistoryCards, {
        data: request,
      });
      return response.data;
    },
    onSuccess: (data) => {
      const deletedCardId = data.cardId;

      // profileViewers 캐시에서 삭제된 카드 제거
      queryClient.setQueryData<InfiniteData<GetProfileViewersResponse>>(
        [...HISTORY_QUERY_KEYS.cards(), 'profileViewers'],
        (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              cards: page.cards.filter((card: ProfileViewCard) => card.id !== deletedCardId),
            })),
          };
        }
      );
    },
    onError: () => {
      showCommonErrorToast();
    },
  });
};

export const useDeleteHistoryCardMutation = () => {
  const queryClient = useQueryClient();
  const showCommonErrorToast = useSetAtom(showCommonErrorToastAtom);

  return useMutation({
    mutationFn: async (request: DeleteHistoryCardRequest) => {
      const response = await client.delete<DeleteHistoryCardResponse>(HistoryApiList.HistoryCards, {
        data: request,
      });
      return response.data;
    },
    onSuccess: (data) => {
      const deletedCardId = data.cardId;

      // 1) 필터별 infinite 목록은 즉시 제거하여 사용자에게 바로 반영한다.
      //    `historyCardsAll()` prefix 로 필터 쿼리만 타겟되어 shape 가드가 필요 없다.
      queryClient.setQueriesData<InfiniteData<GetHistoryCardsResponse>>(
        { queryKey: HISTORY_QUERY_KEYS.historyCardsAll() },
        (old) => {
          if (!old) return old;
          // profileViewers 캐시는 page.data가 없으므로 건너뜀
          if (!old.pages[0] || !('data' in old.pages[0])) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              data: page.data.filter((card) => card.cardId !== deletedCardId),
            })),
          };
        }
      );

      // 2) Recommended 도 "삭제된 카드를 감춘다" 의미로 클라이언트에서 바로 제거.
      //    refetch 로 큐레이션을 다시 받아올 이유가 없고 (서버 왕복 절약),
      //    recommended 쿼리는 AxiosResponse 형태로 캐시되어 `.data` 에 배열이 들어있다.
      queryClient.setQueryData<AxiosResponse<GetRecommendedHistoryCardsResponse>>(
        HISTORY_QUERY_KEYS.historyRecommended(),
        (old) => {
          if (!old) return old;
          return {
            ...old,
            data: old.data.filter((card) => card.cardId !== deletedCardId),
          };
        }
      );
    },
    onError: () => {
      showCommonErrorToast();
    },
  });
};

/**
 * follow 토글 성공 후 History 도메인의 모든 캐시(recommended, 필터별 historyCards, profileViewers)에
 * 동일 유저의 카드를 일괄 반영한다.
 *
 * - `cardUserKey` 기준으로만 매칭해 동일 유저의 모든 카드를 업데이트한다.
 * - `cardUserKey` 가 없으면 아무것도 갱신하지 않는다.
 */
export const syncAllHistoryFollowCaches = (
  queryClient: QueryClient,
  params: {
    cardUserKey: string | undefined;
    nextFollowState: string | undefined;
  }
) => {
  const { cardUserKey, nextFollowState } = params;
  if (!cardUserKey) return;

  const updateCardIfMatches = <T extends Pick<HistoryCard, 'cardUserKey' | 'followState'>>(
    card: T
  ): T => {
    if (card.cardUserKey !== cardUserKey) return card;
    if (card.followState === nextFollowState) return card;
    return { ...card, followState: nextFollowState };
  };

  queryClient.setQueryData<AxiosResponse<GetRecommendedHistoryCardsResponse>>(
    HISTORY_QUERY_KEYS.historyRecommended(),
    (old) => {
      if (!old) return old;
      const data = old.data.map(updateCardIfMatches);
      return data.some((card, i) => card !== old.data[i]) ? { ...old, data } : old;
    }
  );

  queryClient.setQueriesData<InfiniteData<GetHistoryCardsResponse>>(
    { queryKey: HISTORY_QUERY_KEYS.historyCardsAll() },
    (old) => {
      if (!old) return old;
      const pages = old.pages.map((page) => {
        const data = page.data.map(updateCardIfMatches);
        return data.some((card, i) => card !== page.data[i]) ? { ...page, data } : page;
      });
      return pages.some((page, i) => page !== old.pages[i]) ? { ...old, pages } : old;
    }
  );

  queryClient.setQueryData<InfiniteData<GetProfileViewersResponse>>(
    [...HISTORY_QUERY_KEYS.cards(), 'profileViewers'],
    (old) => {
      if (!old) return old;
      const pages = old.pages.map((page) => {
        const cards = page.cards.map(updateCardIfMatches);
        return cards.some((card, i) => card !== page.cards[i]) ? { ...page, cards } : page;
      });
      return pages.some((page, i) => page !== old.pages[i]) ? { ...old, pages } : old;
    }
  );
};

/**
 * History(Recent Activity) 도메인에서 프로필 follow 토글을 수행하는 훅.
 *
 * 공용 `useProfileToggleFollowMutation` 을 래핑해 API 호출 + 공용 ProfileCard/FOLLOW 캐시
 * 업데이트를 위임하고, onSuccess 확장 포인트에서 History 전체 캐시 동기화
 * (`syncAllHistoryFollowCaches`) 를 합성한다.
 */
export function useHistoryProfileToggleFollowMutation(args: {
  card: Pick<HistoryCard, 'cardId' | 'cardUserKey' | 'followState'>;
  profileCardType: DTOProfileCard['type'];
  followRequest: Omit<DTOFollowRequest, 'id'>;
}) {
  const { card, profileCardType, followRequest } = args;
  const queryClient = useQueryClient();

  return useProfileToggleFollowMutation(
    {
      id: card.cardId,
      type: profileCardType,
      followState: card.followState as DTOProfileCard['followState'],
    },
    followRequest,
    {
      onSuccess: (nextFollowState) => {
        syncAllHistoryFollowCaches(queryClient, {
          cardUserKey: card.cardUserKey,
          nextFollowState,
        });
      },
    }
  );
}
